import React, { useState, useMemo } from 'react';
import { 
  GraduationCap, 
  Clock, 
  Calendar, 
  Award, 
  TrendingUp, 
  Users, 
  CheckCircle, 
  AlertCircle,
  BookOpen,
  BarChart3,
  Filter,
  Search,
  Download,
  Eye,
  Star
} from 'lucide-react';
import skillData from '../data/skillDevelopmentData.json';

interface Student {
  id: string;
  name: string;
  course: string;
  contactDate: string;
  finishedDate?: string;
  expectedFinish?: string;
  expectedStart?: string;
  certification: boolean;
  certificateId?: string;
  duration?: string;
  grade?: string;
  progress?: number;
  currentModule?: string;
  status?: string;
  priority?: string;
  skills: string[];
}

const SkillDevelopmentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'finished' | 'ongoing' | 'pending'>('ongoing');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const { finished, ongoing, pending } = skillData.skillDevelopment;

  // Auto-calculate statistics from actual data
  const stats = useMemo(() => {
    const totalStudents = finished.length + ongoing.length + pending.length;
    const completedPrograms = finished.length;
    const ongoingPrograms = ongoing.length;
    const pendingPrograms = pending.length;
    
    // Calculate success rate (completed / (completed + ongoing))
    const successRate = totalStudents > 0 ? Math.round((completedPrograms / (completedPrograms + ongoingPrograms)) * 100) || 0 : 0;
    
    // Calculate average completion time from finished programs
    const completedWithDuration = finished.filter(student => student.duration);
    let averageCompletionTime = "N/A";
    
    if (completedWithDuration.length > 0) {
      const totalWeeks = completedWithDuration.reduce((sum, student) => {
        const weeks = parseInt(student.duration?.split(' ')[0] || '0');
        return sum + weeks;
      }, 0);
      const avgWeeks = Math.round((totalWeeks / completedWithDuration.length) * 10) / 10;
      averageCompletionTime = `${avgWeeks} weeks`;
    }

    return {
      totalStudents,
      completedPrograms,
      ongoingPrograms,
      pendingPrograms,
      successRate,
      averageCompletionTime,
      certificationRate: completedPrograms > 0 ? Math.round((finished.filter(s => s.certification).length / completedPrograms) * 100) : 0
    };
  }, [finished, ongoing, pending]);

  const getTabData = () => {
    switch (activeTab) {
      case 'finished': return finished;
      case 'ongoing': return ongoing;
      case 'pending': return pending;
      default: return ongoing;
    }
  };

  const filteredData = getTabData().filter((student: Student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.course.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'finished':
        return <CheckCircle className="w-6 h-6 text-success" />;
      case 'ongoing':
        return <Clock className="w-6 h-6 text-warning" />;
      case 'pending':
        return <AlertCircle className="w-6 h-6 text-error" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getProgressColor = (progress?: number) => {
    if (!progress) return 'bg-muted';
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="border-t border-border pt-16">
      <div className="flex items-center mb-8">
        <GraduationCap className="w-8 h-8 text-primary mr-3" />
        <h2 className="text-3xl md:text-4xl font-bold">Skill Development Dashboard</h2>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Students</p>
              <p className="text-2xl font-bold text-primary">{stats.totalStudents}</p>
            </div>
            <Users className="w-8 h-8 text-primary/60" />
          </div>
        </div>

        <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-success">{stats.completedPrograms}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-success/60" />
          </div>
        </div>

        <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-accent">{stats.successRate}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-accent/60" />
          </div>
        </div>

        <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg. Duration</p>
              <p className="text-2xl font-bold text-warning">{stats.averageCompletionTime}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-warning/60" />
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <input
            type="text"
            placeholder="Search students or courses..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('finished')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'finished'
                ? 'bg-success text-success-foreground'
                : 'bg-card hover:bg-success/10 border border-border'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            Finished ({stats.completedPrograms})
          </button>
          
          <button
            onClick={() => setActiveTab('ongoing')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'ongoing'
                ? 'bg-warning text-warning-foreground'
                : 'bg-card hover:bg-warning/10 border border-border'
            }`}
          >
            <Clock className="w-4 h-4" />
            Ongoing ({stats.ongoingPrograms})
          </button>
          
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'pending'
                ? 'bg-error text-error-foreground'
                : 'bg-card hover:bg-error/10 border border-border'
            }`}
          >
            <AlertCircle className="w-4 h-4" />
            Pending ({stats.pendingPrograms})
          </button>
        </div>
      </div>

      {/* Students Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredData.map((student: Student) => (
          <div
            key={student.id}
            className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border hover:shadow-lg transition-all duration-300 animated-section group cursor-pointer"
            onClick={() => setSelectedStudent(student)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1 group-hover:text-primary transition-colors">
                  {student.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">{student.course}</p>
                <p className="text-xs text-muted-foreground">ID: {student.id}</p>
              </div>
              <div className="flex-shrink-0">
                {getStatusIcon(activeTab)}
              </div>
            </div>

            {/* Progress Bar for Ongoing */}
            {activeTab === 'ongoing' && student.progress && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Progress</span>
                  <span className="text-sm text-muted-foreground">{student.progress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(student.progress)}`}
                    style={{ width: `${student.progress}%` }}
                  ></div>
                </div>
                {student.currentModule && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Current: {student.currentModule}
                  </p>
                )}
              </div>
            )}

            {/* Status for Pending */}
            {activeTab === 'pending' && student.status && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium">Status:</span>
                  <span className={`text-sm ${getPriorityColor(student.priority)}`}>
                    {student.status}
                  </span>
                </div>
                {student.priority && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Priority:</span>
                    <span className={`text-xs font-medium ${getPriorityColor(student.priority)}`}>
                      {student.priority?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Certification Badge for Finished */}
            {activeTab === 'finished' && student.certification && (
              <div className="mb-4">
                <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg">
                  <Award className="w-4 h-4 text-success" />
                  <span className="text-sm font-medium text-success">Certified</span>
                  {student.grade && (
                    <div className="flex items-center gap-1 ml-auto">
                      <Star className="w-3 h-3 text-warning fill-warning" />
                      <span className="text-xs font-medium">{student.grade}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Skills Tags */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {student.skills.slice(0, 3).map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                  >
                    {skill}
                  </span>
                ))}
                {student.skills.length > 3 && (
                  <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                    +{student.skills.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                <span>Started: {new Date(student.contactDate).toLocaleDateString()}</span>
              </div>
              {student.finishedDate && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3" />
                  <span>Finished: {new Date(student.finishedDate).toLocaleDateString()}</span>
                </div>
              )}
              {student.expectedFinish && (
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  <span>Expected: {new Date(student.expectedFinish).toLocaleDateString()}</span>
                </div>
              )}
              {student.expectedStart && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  <span>Expected Start: {new Date(student.expectedStart).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* View Details Button */}
            <div className="mt-4 pt-4 border-t border-border">
              <button className="w-full flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors">
                <Eye className="w-4 h-4" />
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No students found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or select a different category.
          </p>
        </div>
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setSelectedStudent(null)}
        >
          <div 
            className="relative max-w-2xl w-full max-h-[90vh] bg-card rounded-lg overflow-hidden animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{selectedStudent.name}</h3>
                  <p className="text-muted-foreground">{selectedStudent.course}</p>
                </div>
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="font-semibold mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Student ID:</span>
                      <p className="font-medium">{selectedStudent.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Course:</span>
                      <p className="font-medium">{selectedStudent.course}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Contact Date:</span>
                      <p className="font-medium">{new Date(selectedStudent.contactDate).toLocaleDateString()}</p>
                    </div>
                    {selectedStudent.duration && (
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <p className="font-medium">{selectedStudent.duration}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <h4 className="font-semibold mb-3">Skills Covered</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedStudent.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Progress/Status specific info */}
                {selectedStudent.progress && (
                  <div>
                    <h4 className="font-semibold mb-3">Progress Details</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">Overall Progress</span>
                          <span className="text-sm text-muted-foreground">{selectedStudent.progress}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3">
                          <div
                            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(selectedStudent.progress)}`}
                            style={{ width: `${selectedStudent.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      {selectedStudent.currentModule && (
                        <div>
                          <span className="text-muted-foreground">Current Module:</span>
                          <p className="font-medium">{selectedStudent.currentModule}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Certification Info */}
                {selectedStudent.certification && selectedStudent.certificateId && (
                  <div>
                    <h4 className="font-semibold mb-3">Certification</h4>
                    <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                      <div className="flex items-center gap-3">
                        <Award className="w-8 h-8 text-success" />
                        <div>
                          <p className="font-medium text-success">Certificate Awarded</p>
                          <p className="text-sm text-muted-foreground">ID: {selectedStudent.certificateId}</p>
                          {selectedStudent.grade && (
                            <p className="text-sm text-muted-foreground">Grade: {selectedStudent.grade}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillDevelopmentDashboard;